<template>
  <footer class="app-footer">
    <div class="app-footer__container">
      <!-- Left Section: Logo -->
      <div class="app-footer__brand">
        <div class="app-footer__logo">
          <GonkaGameLogo />
        </div>
      </div>

      <!-- Navigation Columns -->
      <nav class="app-footer__nav">
        <div v-for="section in navigationSections" :key="section.title" class="app-footer__nav-section">
          <h4 class="app-footer__nav-title">{{ section.title }}</h4>
          <ul class="app-footer__nav-list">
            <li 
              v-for="item in section.items" 
              :key="item.label"
            >
              <RouterLink
                class="app-footer__nav-link"
                :to="item.route"
              >
                {{ item.label }}
              </RouterLink>
            </li>
          </ul>
        </div>
      </nav>

      <!-- Social Icons -->
      <div class="app-footer__social">
        <button class="app-footer__social-link app-footer__social-link--help" :title="$t('nav.help')">
          <span>?</span>
        </button>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="app-footer__bottom">
      <!-- Locale Selectors -->
      <div class="app-footer__selectors">
        <button class="app-footer__selector" @click="showLanguageModal = true">
          <svg class="app-footer__selector-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="12"
cy="12"
r="10"
stroke="currentColor"
stroke-width="1.5"/>
            <path d="M2 12h20M12 2c2.5 2 4 5 4 10s-1.5 8-4 10c-2.5-2-4-5-4-10s1.5-8 4-10z" stroke="currentColor" stroke-width="1.5"/>
          </svg>
          <span>{{ currentLanguageLabel }}</span>
        </button>

        <button class="app-footer__selector" @click="showCountryModal = true">
          <svg class="app-footer__selector-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="12"
cy="10"
r="3"
stroke="currentColor"
stroke-width="1.5"/>
            <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 8 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8z" stroke="currentColor" stroke-width="1.5"/>
          </svg>
          <span>{{ countryLabel }}</span>
        </button>
      </div>

      <!-- Copyright & Legal -->
      <div class="app-footer__legal">
        <span class="app-footer__copyright">{{ $t('footer.copyright', { year: currentYear }) }}</span>
        <a href="/app/law/privacy" class="app-footer__legal-link">{{ $t('footer.privacyPolicy') }}</a>
        <a href="/app/law/cookies" class="app-footer__legal-link">{{ $t('footer.cookiePolicy') }}</a>
        <a href="/app/law/terms" class="app-footer__legal-link">{{ $t('footer.termsOfUse') }}</a>
      </div>

      <!-- Company Info -->
      <div class="app-footer__company">
        {{ $t('footer.companyInfo') }}
      </div>

      <!-- Disclaimer -->
      <div class="app-footer__disclaimer">
        {{ $t('footer.disclaimer') }}
      </div>
    </div>

    <!-- Modals -->
    <LanguageSelectionModal
      :visible="showLanguageModal"
      @close="showLanguageModal = false"
      @select="onLanguageSelect"
    />

    <FooterCountryModal
      :visible="showCountryModal"
      @close="showCountryModal = false"
      @select="onCountrySelect"
    />
  </footer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { loadLocale } from '@/i18n'
import { getLocaleConfig } from '@/i18n/config'
import GonkaGameLogo from '@/components/common/GonkaGameLogo.vue'
import LanguageSelectionModal from './LanguageSelectionModal.vue'
import FooterCountryModal from './FooterCountryModal.vue'
import { useProfileStore } from '@/modules/profile/store/profileStore'

const { locale, t } = useI18n()
const profileStore = useProfileStore()

const showLanguageModal = ref(false)
const showCountryModal = ref(false)

const selectedLanguage = computed(() => locale.value)

const currentYear = new Date().getFullYear()

const currentLanguageLabel = computed(() => {
  const config = getLocaleConfig(selectedLanguage.value)
  return config?.nativeName || selectedLanguage.value
})

const countryLabel = computed(() => {
  const countryObj = profileStore.profile?.country
  if (!countryObj) return t('nav.allRegions')
  // Use i18n key for translated country name
  return t(`countries.${countryObj.code}`)
})

function onLanguageSelect(code: string) {
  loadLocale(code)
}

function onCountrySelect(_payload: { code: string; name: string }) {
  // Country is updated through profileStore
}

// Navigation sections for retained GonkaGame Sprint A surface
type FooterNavItem = { label: string; route: string }

// Все разделы ниже ведут в кабинет — гостям их не показываем.
const navigationSections = computed<{ title: string; items: FooterNavItem[] }[]>(() => {
  if (!profileStore.profile) {
    return []
  }

  return [
    {
      title: t('nav.affiliate'),
      items: [
        { label: t('nav.invitation'), route: '/affiliate/partner' },
        { label: t('nav.referrals'), route: '/affiliate/teams' },
      ],
    },
    {
      title: t('nav.sections.accounts'),
      items: [
        { label: t('nav.profile'), route: '/profile' },
        { label: t('nav.wallet'), route: '/wallet' },
        { label: t('nav.history'), route: '/wallet/history' },
      ],
    },
  ]
})

</script>

<style scoped>
.app-footer {
  background: var(--hm-bg-base);
  padding: 40px 40px 24px;
  margin-top: auto;
}

@media (max-width: 768px) {
  .app-footer {
    padding: 24px 16px 100px; /* Extra bottom padding for bottom nav */
  }
}

.app-footer__container {
  display: flex;
  gap: 48px;
  max-width: 1400px;
  margin: 0 auto;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--ds-overlay-08);
}

@media (max-width: 768px) {
  .app-footer__container {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding-bottom: 20px;
  }
}

.app-footer__brand {
  flex-shrink: 0;
}

.app-footer__logo {
  width: 120px;
  height: 24px;
  color: var(--hm-text-primary);
}

@media (max-width: 768px) {
  .app-footer__logo {
    width: 100px;
    height: 20px;
  }
}

.app-footer__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  flex: 1;
}

@media (max-width: 768px) {
  .app-footer__nav {
    display: none;
  }
}

.app-footer__nav-section {
  min-width: 120px;
}

.app-footer__nav-title {
  font-size: var(--p-text-10);
  font-weight: var(--ds-weight-semibold);
  letter-spacing: 0.05em;
  color: var(--ds-overlay-40);
  text-transform: uppercase;
  margin: 0 0 12px;
}

.app-footer__nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.app-footer__nav-link {
  font-size: var(--p-text-13);
  color: var(--ds-overlay-70);
  text-decoration: none;
  transition: color 0.15s;
}

.app-footer__nav-link:hover {
  color: var(--hm-text-primary);
}

.app-footer__nav-link--disabled {
  opacity: 0.4;
  cursor: default;
  position: relative;
}

.app-footer__nav-link--disabled:hover {
  color: var(--ds-overlay-70);
}

.app-footer__social {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

@media (max-width: 768px) {
  .app-footer__social {
    gap: 10px;
    justify-content: center;
  }
}

.app-footer__social-link {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--ds-overlay-08);
  color: var(--ds-overlay-70);
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}

@media (max-width: 768px) {
  .app-footer__social-link {
    width: 40px;
    height: 40px;
  }
}

.app-footer__social-link:hover {
  background: var(--ds-overlay-15);
  color: var(--hm-text-primary);
}

.app-footer__social-link--help {
  font-size: var(--ds-text-size-body-lg);
  font-weight: var(--ds-weight-bold);
}

/* Bottom Bar */
.app-footer__bottom {
  display: flex;
  align-items: center;
  gap: 24px;
  padding-top: 20px;
  max-width: 1400px;
  margin: 0 auto;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .app-footer__bottom {
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  }
}

.app-footer__selectors {
  display: flex;
  gap: 8px;
}

@media (max-width: 768px) {
  .app-footer__selectors {
    justify-content: center;
  }
}

.app-footer__selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: var(--p-radius-50);
  background: var(--ds-overlay-06);
  border: none;
  cursor: pointer;
  color: var(--ds-text);
  font-size: var(--p-text-13);
  font-weight: var(--ds-weight-medium);
  transition: all 0.15s;
}

.app-footer__selector:hover {
  background: var(--ds-overlay-12);
  color: var(--hm-text-primary);
}

.app-footer__selector-icon {
  width: 18px;
  height: 18px;
}

.app-footer__legal {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

@media (max-width: 768px) {
  .app-footer__legal {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 8px 12px;
  }
}

.app-footer__copyright {
  font-size: var(--ds-text-size-caption);
  color: var(--ds-overlay-40);
}

.app-footer__legal-link {
  font-size: var(--ds-text-size-caption);
  color: var(--ds-overlay-40);
  text-decoration: none;
  transition: color 0.15s;
}

.app-footer__legal-link:hover {
  color: var(--ds-overlay-70);
}

.app-footer__company {
  font-size: var(--p-text-11);
  color: var(--ds-overlay-30);
  text-align: right;
}

@media (max-width: 768px) {
  .app-footer__company {
    text-align: center;
  }
}

.app-footer__disclaimer {
  grid-column: 1 / -1;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid var(--ds-overlay-08);
  font-size: var(--ds-text-size-caption);
  line-height: 1.5;
  color: var(--ds-overlay-25);
  text-align: left;
}

@media (max-width: 768px) {
  .app-footer__disclaimer {
    text-align: justify;
    font-size: var(--ds-text-size-caption);
    line-height: 1.6;
    padding: 16px 0 0;
    margin-top: 16px;
  }
}

/* Responsive */
@media (max-width: 1200px) {
  .app-footer__container {
    flex-wrap: wrap;
  }
  
  .app-footer__nav {
    order: 3;
    width: 100%;
    margin-top: 24px;
  }
}

@media (max-width: 992px) {
  .app-footer__bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .app-footer__legal {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .app-footer__company {
    text-align: left;
    width: 100%;
  }
}
</style>
